import React, { createContext, useContext } from 'react';
import cn from '@/utils/cn';
import { ancestorItemClass } from '../railClasses';
import { RailPage, isCurrentPage } from './contentPages';

export interface RailLinkProps {
  // Forced by the tree for structural rows; page links leave it unset and the link decides from
  // the location.
  active?: boolean;
  // The row is a unit or lesson the current page sits inside.
  ancestor?: boolean;
  children: React.ReactNode;
  className: (active: boolean) => string;
  page: RailPage;
}

export const railLinkState = ({ active, ancestor, className, page }: Omit<RailLinkProps, 'children'>) => {
  const isActive = active ?? isCurrentPage(page);
  const isAncestor = !isActive && Boolean(ancestor);
  let current: 'page' | 'location' | undefined;
  if (isActive) current = 'page';
  else if (isAncestor) current = 'location';

  return { className: cn(className(isActive), isAncestor && ancestorItemClass), current };
};

export type RailLinkComponent = (props: RailLinkProps) => JSX.Element;

// Plain anchors: every destination is a full page load (the legacy content rail).
export const AnchorRailLink = ({ children, ...props }: RailLinkProps) => {
  const { className, current } = railLinkState(props);

  return (
    <a aria-current={current} className={className} data-method={props.page.method} href={props.page.href}>
      {children}
    </a>
  );
};

const RailLinkContext = createContext<RailLinkComponent>(AnchorRailLink);

export const RailLinkProvider = RailLinkContext.Provider;

export const useRailLink = () => useContext(RailLinkContext);

interface PageLinksProps {
  className?: (active: boolean) => string;
  pages: RailPage[];
}

export const PageLinks = ({ className, pages }: PageLinksProps) => {
  const RailLink = useRailLink();

  return (
    <>
      {pages.map(page => (
        <RailLink key={page.label} className={className ?? (() => '')} page={page}>
          {page.label}
        </RailLink>
      ))}
    </>
  );
};
